/**
 * Resource Permissions Configuration
 * 
 * ZERO-CONFIG DYNAMIC MAPPING - No manual mapping needed!
 * 
 * Strategy:
 * 1. Try direct resource name as permission prefix (e.g., 'organizations' → 'organizations:list')
 * 2. If that fails, try common variations:
 *    - Remove 's' for plural (e.g., 'codes' → 'code:list')
 *    - Handle special cases via naming convention inference
 * 
 * The permission check will try multiple variations and use the first one that exists
 * in the user's permission set.
 * 
 * When RBAC is disabled (enableAdminPanelRBAC = false):
 * - All resources are accessible (backward compatibility)
 * 
 * When RBAC is enabled (enableAdminPanelRBAC = true):
 * - Dynamically checks all possible permission variations
 */

/**
 * Action mapping - maps React-Admin actions to permission actions
 */
const ACTION_MAPPING = {
  'list': 'list',
  'show': 'read',
  'create': 'create',
  'edit': 'update',
  'delete': 'delete',
};

/**
 * Generate possible permission names for a resource and action
 * Uses intelligent heuristics to guess the correct permission name
 * 
 * @param {string} resource - Resource name (e.g., 'patients', 'organizations')
 * @param {string} action - Action type ('list', 'show', 'create', 'edit', 'delete')
 * @returns {Array<string>} - List of possible permission names to check
 * 
 * @example
 * getPossiblePermissions('patients', 'list') 
 * // Returns: ['patients:list', 'patient:list', 'users:list', 'user:list']
 * 
 * getPossiblePermissions('organizations', 'show')
 * // Returns: ['organizations:read', 'organization:read']
 */
const getPossiblePermissions = (resource, action) => {
  const permissionAction = ACTION_MAPPING[action] || action;
  const possibilities = [];
  
  // Strategy 1: Use resource name as-is
  possibilities.push(`${resource}:${permissionAction}`);
  
  // Strategy 2: Remove trailing 's' if plural (e.g., 'organizations' → 'organization')
  if (resource.endsWith('s') && resource.length > 1) {
    const singular = resource.slice(0, -1);
    possibilities.push(`${singular}:${permissionAction}`);
  }
  
  // Strategy 3: Special case mappings based on common patterns
  const specialMappings = {
    // User resources (patients, providers, admins) → 'users'
    'patients': ['users', 'user'],
    'providers': ['users', 'user'],
    'admins': ['users', 'user'],
    
    // Resource-specific aliases
    'lead-imports': ['lead-imports', 'leadImports', 'lead_imports'],
    'product-features': ['product-features', 'roles', 'features'],
    'cms1500-jobs': ['cms1500-jobs', 'cms1500', 'billing'],
    'hotline-blocklist': ['hotline-blocklist', 'hotline'],
    
    // Healthie resources - NOTE: These use :read instead of :list
    'healthie-appointment-types': ['healthie:appointment_types'],
    'provider-availability': ['healthie:provider'],
  };
  
  if (specialMappings[resource]) {
    specialMappings[resource].forEach(prefix => {
      possibilities.push(`${prefix}:${permissionAction}`);
    });
  }
  
  // Strategy 4: Try 'hotline' fallback for hotline (uses calls permissions)
  if (resource === 'hotline') {
    possibilities.push(`calls:${permissionAction}`);
  }
  
  return [...new Set(possibilities)]; // Remove duplicates
};

/**
 * Get required permission for a resource action
 * Returns the FIRST matching permission from possible variations
 * 
 * @param {string} resource - Resource name (e.g., 'patients', 'organizations')
 * @param {string} action - Action type ('list', 'show', 'create', 'edit', 'delete')
 * @param {Array<string>} userPermissions - User's current permissions (optional, for smart matching)
 * @returns {string|null} - Required permission or null if not determinable
 * 
 * @example
 * getResourcePermission('patients', 'list', ['users:list', 'organizations:read'])
 * // Returns: 'users:list' (matches from possibilities)
 */
export const getResourcePermission = (resource, action = 'list', userPermissions = []) => {
  const possibilities = getPossiblePermissions(resource, action);
  
  // If we have user permissions, return the first one that matches
  if (userPermissions.length > 0) {
    const match = possibilities.find(perm => userPermissions.includes(perm));
    return match || possibilities[0]; // Return match or first possibility
  }
  
  // Otherwise return the first possibility (most likely to be correct)
  return possibilities[0];
};

/**
 * Check if a resource should be visible in navigation
 * Tries all possible permission variations until one matches
 * 
 * @param {string} resource - Resource name
 * @param {function} hasPermission - Permission check function from usePermissions()
 * @returns {boolean} - True if user can view this resource
 */
export const canViewResource = (resource, hasPermission) => {
  // Try both 'list' and 'read' actions (Healthie resources use 'read' instead of 'list')
  const listPossibilities = getPossiblePermissions(resource, 'list');
  const readPossibilities = getPossiblePermissions(resource, 'show');
  const allPossibilities = [...listPossibilities, ...readPossibilities];
  
  // Check if user has ANY of the possible permissions
  return allPossibilities.some(perm => hasPermission(perm));
};

/**
 * Check if user can perform an action on a resource
 * Tries all possible permission variations until one matches
 * 
 * @param {string} resource - Resource name
 * @param {string} action - Action type ('list', 'show', 'create', 'edit', 'delete')
 * @param {function} hasPermission - Permission check function from usePermissions()
 * @returns {boolean} - True if user can perform this action
 */
export const canPerformAction = (resource, action, hasPermission) => {
  const possibilities = getPossiblePermissions(resource, action);
  
  // Check if user has ANY of the possible permissions
  return possibilities.some(perm => hasPermission(perm));
};

/**
 * Debug helper: Get all possible permission names for a resource
 * Useful for troubleshooting permission issues
 * 
 * @param {string} resource - Resource name
 * @returns {Object} - Map of actions to possible permissions
 */
export const debugResourcePermissions = (resource) => {
  const actions = ['list', 'show', 'create', 'edit', 'delete'];
  const result = {};
  
  actions.forEach(action => {
    result[action] = getPossiblePermissions(resource, action);
  });
  
  return result;
};
